import React, { useState, useEffect, useMemo } from 'react';
import {
  Flex,
  Text,
  Box,
  Button,
  useDisclosure,
  useToast,
  Input,
  InputGroup,
  InputRightElement,
  InputLeftElement,
  FormControl,
  FormErrorMessage,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useHistory, useLocation } from 'react-router-dom';
import _ from 'lodash';

import PlanEditer, { PlanFormData } from '../../Components/Plan/PlanEditer';
import { CancelEditModal } from './CreatePlanPage';
import { PlanType, editPlan } from '../../Utils/backStageApi';
import { checkAuth } from '../../Utils/common';

const EditPlanPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();

  const plan = (location.state as { plan: PlanType } | undefined)?.plan;

  const [lastData] = useState<PlanFormData | undefined>(() =>
    plan
      ? {
          code: plan.code,
          name_zh: plan.name_zh,
          annual_limit: plan.annual_limit === null ? '' : String(plan.annual_limit),
        }
      : undefined,
  );

  const [formData, setFormData] = useState<PlanFormData | undefined>(lastData);
  const [password, setPassword] = useState({
    text: '',
    visible: false,
    isError: false,
    errorMsg: '密碼錯誤',
  });
  const [modalMode, setModalMode] = useState<string>('update');
  const { isOpen: cancelIsOpen, onOpen: openCancel, onClose: closeCancel } = useDisclosure();
  const { isOpen: confirmIsOpen, onOpen: openConfirm, onClose: closeConfirm } = useDisclosure();

  const isDirty = useMemo(() => !_.isEqual(lastData, formData), [lastData, formData]);
  const isSubmittable = useMemo(() => !!formData?.name_zh.trim(), [formData]);

  useEffect(() => {
    checkAuth();

    if (!plan) {
      history.goBack();
    }
  }, []);

  if (!plan) return null;

  const cancelEdit = () => {
    if (_.isEqual(lastData, formData)) history.goBack();
    else openCancel();
  };

  const openPasswordModal = (mode: string) => {
    setModalMode(mode);
    openConfirm();
  };

  const handleUpdate = () => {
    setPassword((prev) => ({ ...prev, isError: false }));
    if (!formData) return;

    const payload: { plan_id: number; name_zh?: string; annual_limit?: string } = { plan_id: plan.id };
    if (formData.name_zh.trim() !== lastData?.name_zh) payload.name_zh = formData.name_zh.trim();
    if (formData.annual_limit.trim() !== lastData?.annual_limit && formData.annual_limit.trim() !== '') {
      payload.annual_limit = formData.annual_limit.trim();
    }

    editPlan(password.text, payload)
      .then((response) => {
        if (response && response.applied === true) {
          closeConfirm();
          toast({ title: '方案已更新', status: 'success', duration: 5000, isClosable: true });
          history.goBack();
        } else {
          setPassword((prev) => ({ ...prev, isError: true, errorMsg: response?.error_msg || '密碼錯誤' }));
        }
      })
      .catch(() => {
        setPassword((prev) => ({ ...prev, isError: true, errorMsg: '發生錯誤，請稍後再試' }));
      });
  };

  const handleRemove = () => {
    setPassword((prev) => ({ ...prev, isError: false }));
    editPlan(password.text, { plan_id: plan.id })
      .then((response) => {
        if (response && response.applied === true) {
          closeConfirm();
          toast({ title: '方案已移除', status: 'success', duration: 5000, isClosable: true });
          history.goBack();
        } else {
          setPassword((prev) => ({ ...prev, isError: true, errorMsg: response?.error_msg || '密碼錯誤' }));
        }
      })
      .catch(() => {
        setPassword((prev) => ({ ...prev, isError: true, errorMsg: '發生錯誤，請稍後再試' }));
      });
  };

  const canRemove = plan.issuer_count === 0;

  return (
    <Flex w="80vw" mx="auto" pt="50px" pb="100px" justify="center" alignItems="flex-start">
      <Flex maxW="679px" w="100%" direction="column">
        <Box>
          <Text fontWeight="700" fontSize="30px" lineHeight="133%" color="#000000" textAlign="center">
            編輯方案
          </Text>
        </Box>
        <Box mt="75px">
          <PlanEditer initData={lastData} disableCode setFormData={setFormData} />
        </Box>
        {!canRemove && (
          <Text mt="16px" fontSize="14px" color="#C53030">
            尚有 {plan.issuer_count} 個機構使用此方案，無法刪除
          </Text>
        )}
        <Flex mt="60px" justify="center" gap="30px">
          <Button
            variant="outlinedCritical"
            p="8px 12px"
            border="1px solid"
            isDisabled={!canRemove}
            onClick={() => openPasswordModal('remove')}
          >
            移除
          </Button>
          <Button
            p="8px 12px"
            onClick={() => openPasswordModal('update')}
            isDisabled={!isDirty || !isSubmittable}
            _disabled={{ opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'auto' }}
            _hover={{ opacity: 0.85 }}
          >
            更新
          </Button>
        </Flex>
      </Flex>
      <CancelEditModal isOpen={cancelIsOpen} onClose={closeCancel} />
      <Modal isOpen={confirmIsOpen} onClose={closeConfirm} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent maxW="540px">
          <ModalHeader p="24px 24px 16px 24px">
            <Text textStyle="semibold_lg" color={modalMode === 'update' ? 'text.main' : 'critical.300'}>
              請輸入密碼進行{modalMode === 'update' ? '更新' : '移除'}
            </Text>
          </ModalHeader>
          <ModalBody p="0 24px 16px 24px" color="grey.700">
            <FormControl w="80%" isInvalid={password.isError}>
              <InputGroup>
                <InputLeftElement color="text.main">
                  <Text textStyle="semibold_sm">密碼</Text>
                </InputLeftElement>
                <Input
                  type={password.visible ? 'text' : 'password'}
                  name="password"
                  placeholder="請輸入密碼"
                  onChange={(e) => setPassword((prev) => ({ ...prev, text: e.target.value }))}
                  value={password.text}
                />
                <InputRightElement
                  cursor="pointer"
                  onClick={() => setPassword((prev) => ({ ...prev, visible: !prev.visible }))}
                >
                  {password.visible ? <ViewOffIcon color="primary.main" /> : <ViewIcon color="primary.main" />}
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{password.errorMsg}</FormErrorMessage>
            </FormControl>
          </ModalBody>
          <ModalFooter p="0 24px 24px 24px" gap="8px">
            <Button variant="secondary" onClick={closeConfirm}>
              返回
            </Button>
            <Button
              variant={modalMode === 'update' ? 'primary' : 'critical'}
              onClick={() => (modalMode === 'update' ? handleUpdate() : handleRemove())}
              isDisabled={!password.text}
            >
              {modalMode === 'update' ? '更新' : '移除'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default EditPlanPage;
